/**
 * Single source of truth for every price, prize and cap.
 * The provider reads amounts from here — never from the caller — so a page
 * (or a browser console) can no longer pass its own price or prize.
 *
 * Design rule (owner's decision): the NUMBER of ads is never capped.
 * What is capped is the ATH minted per day; beyond the cap every ad still
 * gives something real (Turbo progress, spins, skips).
 */

// ─────────────────────────── Ads ───────────────────────────

/** ATH per Ad Hub ad, by position in today's count (1-based). */
export const AD_LADDER: { upTo: number; ath: number }[] = [
  { upTo: 10, ath: 0.3 },
  { upTo: 20, ath: 0.5 },
  { upTo: 30, ath: 0.8 },
];
/** One-off bonus when the 30th ad of the day is watched. */
export const AD_LADDER_BONUS_AT = 30;
export const AD_LADDER_BONUS = 4;
/** ATH per ad after the ladder, until the daily ATH cap. */
export const AD_ATH_AFTER_LADDER = 0.05;
/** Daily ATH cap for Ad Hub ads (ladder 16 + bonus 4 + tail). */
export const ADS_DAILY_ATH_CAP = 25;
/** Ads watched (any placement) that trigger Turbo: 5x grants for 1 hour. */
export const TURBO_ADS_REQUIRED = 10;

export function adRewardForCount(n: number): number {
  for (const tier of AD_LADDER) if (n <= tier.upTo) return tier.ath;
  return AD_ATH_AFTER_LADDER;
}

// ─────────────────────────── Grant ───────────────────────────

/** Ad boost on a claim: 1 ad → 1.5x, 2 ads → 2x. */
export const GRANT_AD_BOOSTS: Record<number, number> = { 0: 1, 1: 1.5, 2: 2 };
/** Hard cap on the product of every multiplier applied to a grant. */
export const MAX_GRANT_MULTIPLIER = 20;
/** Cooldown skips per day; the n-th skip costs n ads (or INSTANT_REFILL_COST Pi). */
export const SKIPS_PER_DAY = 5;
export const INSTANT_REFILL_COST = 30;

// ─────────────────────────── Games ───────────────────────────

/** Daily ATH cap for the four games combined (rounds themselves are unlimited). */
export const GAMES_DAILY_ATH_CAP = 10;
/** @deprecated kept for older imports; use GAMES_DAILY_ATH_CAP / ADS_DAILY_ATH_CAP */
export const DAILY_GAME_ATH_CAP = GAMES_DAILY_ATH_CAP;

// ─────────────────────────── Spin ───────────────────────────

export type SpinRewardType = "points" | "picoin" | "multiplier" | "empty";

export interface SpinReward {
  label: string;
  value: number;
  type: SpinRewardType;
  weight: number; // higher = more likely
}

/** Order matters: it is the order of the segments on the wheel. */
export const SPIN_REWARDS: SpinReward[] = [
  { label: "1 ATH", value: 1, type: "points", weight: 25 },
  { label: "0.001 Pi", value: 0.001, type: "picoin", weight: 2 },
  { label: "5 ATH", value: 5, type: "points", weight: 20 },
  { label: "Retry", value: 0, type: "empty", weight: 15 },
  { label: "10 ATH", value: 10, type: "points", weight: 10 },
  { label: "0.01 Pi", value: 0.01, type: "picoin", weight: 2 },
  { label: "2 ATH", value: 2, type: "points", weight: 22 },
  { label: "Lucky Tag", value: 1, type: "multiplier", weight: 4 },
];

export const SPIN_COOLDOWN_MS = 24 * 60 * 60 * 1000;
/** Daily ATH cap from spins (extra spins via ads are unlimited; Pi and Lucky Tag are never capped). */
export const SPIN_DAILY_ATH_CAP = 15;

// ─────────────────────────── Shop ───────────────────────────

/** Forge boosts, in Pi. Items not listed here cannot be bought. */
export const FORGE_PRICES: Record<string, number> = {
  streak_resurrection: 50,
  flash_boost: 25,
  turbo_charge: 100,
  instant_refill: 30,
  mega_flash_boost: 200,
};

/** Mystery boxes, in Pi. */
export const BOX_PRICES: Record<string, number> = {
  bronze_box: 10,
  silver_box: 25,
  gold_box: 50,
  diamond_box: 100,
  whale_box: 500,
};

/** Rewarded ads that unlock a free bronze box (persisted on the user doc). */
export const FREE_BOX_ADS_REQUIRED = 3;

/** Home "Flash Boost" card, in Pi. */
export const FLASH_BOOST_COST = 5;

/** Stake boost: 1 + min(STAKE_BOOST_MAX_EXTRA, pi / STAKE_PI_PER_1X). 100 π → 2x, 300 π+ → 4x. */
export const STAKE_PI_PER_1X = 100;
export const STAKE_BOOST_MAX_EXTRA = 3;

/** Deposit bonuses, credited in completePayment. */
export const FIRST_DEPOSIT_MIN_PI = 10; // first deposit ≥ 10 π → Flash Boost 24h
export const TURBO_DEPOSIT_MIN_PI = 100; // any deposit ≥ 100 π → Turbo 24h

/** Pro perks. */
export const PRO_MONTHLY_PI = 500;
export const PRO_LIFETIME_PI = 5000;
